import { ChangeDetectionStrategy, Component, input, signal, effect, inject } from '@angular/core';
import { CommonModule, KeyValue } from '@angular/common';
import { Product, Variant, VariantValue } from '../../models/product.model';

import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
// Using native JavaScript instead of lodash for better tree-shaking

// Deep comparison helper function
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (!deepEqual(a[i], b[i])) return false;
  }
  return true;
}

import { getCurrentProductVariantDetail } from 'src/app/components/product-list-card/product-list-card.component';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { SIZE_ORDER } from 'src/app/core/constants/size-constants';

export const NON_DYNAMIC_VARIANTS: string[] = [];

export enum AttributeType {
  Color = 'Color',
  Size = 'Size',
  SizeUK = 'Size(UK)',
  SizeUS = 'Size(US)',
  SizeCM = 'Size(CM)',
  ApparelSize = 'ApparelSize',
  Other = 'Other'
}

export interface Option {
  option: string;
  variant: Variant;
}

@Component({
  standalone: true,
  selector: 'webstore-dynamic-variants',
  templateUrl: './dynamic-variants.component.html',
  styleUrls: ['./dynamic-variants.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule]
})
export class DynamicVariantsComponent {
  private router = inject(Router);


  IMAGE_URLS = IMAGE_URLS;



  readonly attributeSelectionsInput = input<FormGroup>({} as FormGroup);
  private _attributeSelections = signal<FormGroup>({} as FormGroup);

  get attributeSelections() {
    return this._attributeSelections();
  }

  constructor() {
    effect(() => {
      const attributeSelectionsValue = this.attributeSelectionsInput();
      this._attributeSelections.set(attributeSelectionsValue);
    });

    effect(() => {
      const productValue = this.productInput();
      if (productValue) {
        this._product.set(productValue);
        if (!this.isSetup) {
          this.initializeBuckets(productValue);
        } else {
          this.populateVariantBucketsV2(this.activeVariants);
        }
        this.isSetup = true;
      }
    });
  }

  isSetup = false;
  AttributeType = AttributeType;
  attributesMap = new Map<string, Map<string, Variant>>();
  variantOfCurrentProduct!: Variant;
  lastAttribute = null;
  allOptionsUnavailable = false;
  attributesCombinations!: { key: string, value: string }[][];
  primaryAttributeProductVariants!: { key: string, value: string }[][];
  variantProductPresent = true;
  lastSelectedUnavailableAttribute = "";

  keyAscOrder = (a: KeyValue<string, Variant>, b: KeyValue<string, Variant>): number => {
    if (isNaN(+a.key)) {
      return a.key > b.key ? 1 : (b.key > a.key ? -1 : 0);
    }
    return +a.key - +b.key;
  }

  apparelSize = (a: KeyValue<string, Variant>, b: KeyValue<string, Variant>): number => {
    const comparator1 = SIZE_ORDER.includes(a.key) ? SIZE_ORDER.indexOf(a.key) : Number.MAX_SAFE_INTEGER;
    const comparator2 = SIZE_ORDER.includes(b.key) ? SIZE_ORDER.indexOf(b.key) : Number.MAX_SAFE_INTEGER;
    return comparator1 - comparator2;
  }

  alphabetAndNumberAscOrder = (a: KeyValue<string, Variant>, b: KeyValue<string, Variant>): number => {
    if (isNaN(+a.key)) {
      return a.key > b.key ? 1 : (b.key > a.key ? -1 : 0);
    }
    return +a.key - +b.key;
  }

  asIs = (a: KeyValue<string, Map<string, Variant>>, b: KeyValue<string, Map<string, Variant>>): number => {
    return 0;
  }


  readonly productInput = input<Product>({} as Product);
  private _product = signal<Product>({} as Product);

  get product(): Product {
    return this._product();
  }

  get variants() {
    return this.product?.variants;
  }

  get activeVariants() {
    const activeProductVariants = (this.product?.variants?.filter(variant => variant?.is_active && variant?.is_visible) ?? [])

    activeProductVariants?.forEach(variants => {
      if (variants?.variant_values) {
        variants.variant_values = variants?.variant_values?.map(variants => this.reorderObject(variants))?.sort((variant1, variant2) => variant1?.key < variant2?.key ? -1 : variant1?.key > variant2?.key ? 1 : 0);
      }
    });
    return activeProductVariants;
  }

  get variantValues(): VariantValue[] {
    return this.product?.variant_values;
  }

  private prepareVariantBucketsV2(variants: Variant[]) {

    /* Clearing already added attribute map  */
    this.attributesMap.clear();

    const firstVariant = variants?.[0];
    if (!firstVariant) return;

    const attributes = this.getAttributes(firstVariant);

    const primaryAttribute = this.product.variant_group?.primary_attribute ?? '';

    attributes.sort(attribute => {
      if (attribute === primaryAttribute) {
        return -1;
      } else {
        return 1;
      }
    });

    /* Creating attribute map */
    attributes.forEach(attribute => {
      if (!this.attributesMap.has(attribute) && !NON_DYNAMIC_VARIANTS.includes(attribute)) {
        this.attributesMap.set(attribute, new Map());
      }
    });
  }

  private populateVariantBucketsV2(variants: Variant[]) {

    /* Clearing already added variants values */
    this.clearVariantMapValues();

    /* Resetting foundAlternateProduct value  */
    let foundAlternateProduct = false;

    // all product variants
    variants.forEach(variant => {

      // will get all attributes [color,size,...]
      const attributes = this.getAttributes(variant);

      attributes.forEach((attribute, index) => {
        // attribute color
        if (this.attributesMap.has(attribute)) {

          let selectedVariantsArray: { key: string; value: string }[] = [];

          if (index == 0) {
            const foundAttribute = variant.variant_values.find(attributeKeyValue => attributeKeyValue.key === attribute);
            if (foundAttribute) {
              const attributeValue = foundAttribute.value;
              const attributeMap = this.attributesMap.get(attribute);
              if (attributeMap && !attributeMap.has(attributeValue)) {
                attributeMap.set(attributeValue, variant);
              }
            }
          } else if (index !== 0 && this.variantProductPresent && !this.lastSelectedUnavailableAttribute) {
            /* For Available Products combination */
            /* Getting parent level selectedVariant */
            for (let i = 0; i < index; i++) {
              const currentAttribute = attributes[i];
              if (!currentAttribute) continue;
              const selectedVariant = { key: currentAttribute, value: this.attributeSelections.get(currentAttribute)?.value };
              selectedVariantsArray.push(selectedVariant);
            }

            /* Sorting */
            selectedVariantsArray = selectedVariantsArray?.map(selectedVariants => this.reorderObject(selectedVariants));
            const sortedVariantDetail = JSON.parse(JSON.stringify(variant.variant_values))?.map((attribute: any) => this.reorderObject(attribute))?.slice(0, selectedVariantsArray?.length);

            /* Comparing attributes */
            if (JSON.stringify(selectedVariantsArray) === JSON.stringify(sortedVariantDetail)) {
              const foundAttribute = variant.variant_values.find(attributeKeyValue => attributeKeyValue.key === attribute);
              if (foundAttribute) {
                const attributeValue = foundAttribute.value;
                const attributeMap = this.attributesMap.get(attribute);
                if (attributeMap && !attributeMap.has(attributeValue)) {
                  attributeMap.set(attributeValue, variant);
                }
              }
            }
          } else if (index !== 0 && !this.variantProductPresent && this.lastSelectedUnavailableAttribute) {
            /* For Non-available attribute combination */
            /* Checking is alternate product detected */
            if (!foundAlternateProduct) {
              /* Getting alternate product with all selected attribute except last attribute selection */
              const attributes = this.getAttributes(variant);
              let selectedAvailableVariantsArray: { key: string; value: string }[] = [];
              attributes?.forEach((attribute, i) => {
                if (i !== attributes?.length - 1) {
                  const selectedVariant = { key: attribute, value: this.attributeSelections.get(attribute)?.value };
                  selectedAvailableVariantsArray.push(selectedVariant);
                }
              });
              selectedAvailableVariantsArray = selectedAvailableVariantsArray?.map(selectedVariants => this.reorderObject(selectedVariants));
              const alternateProducts = this.activeVariants?.find(variantDetails => {
                const sortedVariantDetail = JSON.parse(JSON.stringify(variantDetails?.variant_values))?.map((attribute: any) => this.reorderObject(attribute))?.slice(0, selectedAvailableVariantsArray?.length);
                return JSON.stringify(selectedAvailableVariantsArray) === JSON.stringify(sortedVariantDetail)
              });

              /* If alternate product detected */
              if (alternateProducts?.id) {
                let changedAttribute;
                /* Getting the value of attribute which value updated in alternate product  */
                for (let i = 0; i < alternateProducts?.variant_values?.length; i++) {
                  const variant = alternateProducts?.variant_values[i];
                  if (variant?.key) {
                    const formControl = this.attributeSelections?.get(variant.key);
                    if (formControl?.value !== variant?.value) {
                      changedAttribute = { key: variant.key, value: variant?.value };
                      break;
                    }
                  }
                }
                /* Setting foundAlternateProduct to true to avoid looping  */
                foundAlternateProduct = true;
                if (changedAttribute && Object.keys(changedAttribute).length) {
                  /* Calling goToProduct method */
                  this.goToProduct(changedAttribute.key, changedAttribute.value);
                }
              } else {
                /* Finding alternate product for last clicked attribute value and it's selected parent attributes */
                let alternateProductsForSelectedAttributes = this.activeVariants?.filter(variant => {
                  if (Array.isArray(variant?.variant_values) && variant?.variant_values?.find(variantDetail => variantDetail?.key === this.lastSelectedUnavailableAttribute && variantDetail?.value === this.attributeSelections.get(this.lastSelectedUnavailableAttribute)?.value)) {
                    return true
                  }
                });

                const indexOfLastClickedAttribute = attributes?.findIndex(attribute => attribute === this.lastSelectedUnavailableAttribute);

                for (let i = indexOfLastClickedAttribute - 1; i >= 0; i--) {
                  const alternateSelectedParentAttributeProducts = alternateProductsForSelectedAttributes?.filter(variant => {
                    if (Array.isArray(variant?.variant_values) && variant?.variant_values?.find(variantDetail => variantDetail?.key === attributes[i] && variantDetail?.value === this.attributeSelections.get(attributes[i] || '')?.value)) {
                      return true
                    }
                  });
                  alternateProductsForSelectedAttributes = alternateSelectedParentAttributeProducts?.length ? alternateSelectedParentAttributeProducts : alternateProductsForSelectedAttributes;
                }

                let changedAttribute;
                /* Getting the value of attribute which value updated in alternate product  */
                if (alternateProductsForSelectedAttributes?.length && alternateProductsForSelectedAttributes[0]?.id) {
                  for (let i = 0; i < alternateProductsForSelectedAttributes[0]?.variant_values?.length; i++) {
                    const variant = alternateProductsForSelectedAttributes[0]?.variant_values[i];
                    if (variant?.key) {
                      const formControl = this.attributeSelections?.get(variant.key);
                      if (formControl?.value !== variant?.value) {
                        changedAttribute = { key: variant?.key, value: variant?.value };
                        break;
                      }
                    }
                  }
                  /* Setting foundAlternateProduct to true to avoid looping  */
                  foundAlternateProduct = true;
                  if (changedAttribute && Object.keys(changedAttribute).length) {
                    /* Calling goToProduct method */
                    this.goToProduct(changedAttribute?.key, changedAttribute?.value);
                  }
                }
              }
            }
          }
        }
      });
    });
  }

  private getAttributes(variant: Variant): string[] {
    if (!variant) {
      return [];
    }
    return variant?.variant_values?.map(attribute => attribute?.key) ?? [];
  }

  /**
   * @todo sorting
   */
  private sortOptions() {
  }

  goToProduct(attribute: any, option: any) {
    /* Retting values */
    this.variantProductPresent = true;
    this.lastSelectedUnavailableAttribute = "";
    /* Checking for same selection click  */
    if (this.attributeSelections.get(attribute)?.value === option) {
      return
    }

    this.allOptionsUnavailable = false;

    // get current values
    const temp = [...this.variantValues];

    // replace with new
    const foundAttribute = temp.find(attributeKeyValue => attributeKeyValue.key === attribute);
    if (foundAttribute) {
      foundAttribute.value = option;
    }
    const newVariantAttributes = [...temp];

    // find a matching variant
    const targetProduct = this.findVariant(newVariantAttributes);

    // If product not found 
    if (!targetProduct) {
      this.variantProductPresent = false;
      this.lastSelectedUnavailableAttribute = attribute;
      this.attributeSelections.get(attribute)?.patchValue(option);
      this.populateVariantBucketsV2(this.activeVariants);
      return;
    }

    /* If Product found */
    if (targetProduct?.is_active && targetProduct?.is_visible) {
      this.attributeSelections.get(attribute)?.patchValue(option);
    }

    this.navigateToProduct(targetProduct?.url_path);
    this.primaryAttributeProductVariants = [];
    this.populateVariantBucketsV2(this.activeVariants);
  }

  private findVariant(newVariantAttributes: VariantValue[]) {

    const sortByOneWay = ((variant1: VariantValue, variant2: VariantValue) => variant1?.key < variant2?.key ? -1 : variant1?.key > variant2?.key ? 1 : 0);
    const allVariants = JSON.parse(JSON.stringify(this.activeVariants));
    return allVariants?.find((variant: Variant) => {
      return deepEqual(newVariantAttributes.sort(sortByOneWay), variant.variant_values.sort(sortByOneWay));
    });
  }

  protected navigateToProduct(url: string) {
    if (!url) {
      return;
    }
    this.router.navigateByUrl(`/products/${url}`, { state: { scrollToTop: false } });
  }

  /* getSizeVariants(sizeVariants: { key: string, value: Variant }[]) {
    const copyArray = [...sizeVariants];
    const sortedSizes = copyArray?.sort((sizeVariant1, sizeVariant2) => {

      const size1 = this.getAttributeValueFromVariantDetails(AttributeType.Size, sizeVariant1.value);
      const size2 = this.getAttributeValueFromVariantDetails(AttributeType.Size, sizeVariant2.value);

      // .sort((s1, s2) => +s1.size - +s2.size);
      return +size1 - +size2;
    });
    return sortedSizes;
  }

  getApparelSizeVariants(sizeVariants: { key: string, value: Variant }[]) {
    return sizeVariants?.sort((sizeVariant1, sizeVariant2) => {

      const size1 = this.getAttributeValueFromVariantDetails(AttributeType.ApparelSize, sizeVariant1.value);
      const size2 = this.getAttributeValueFromVariantDetails(AttributeType.ApparelSize, sizeVariant2.value);

      const comparator1 = SIZE_ORDER.includes(size1) ? SIZE_ORDER.indexOf(size1) : Number.MAX_SAFE_INTEGER;
      const comparator2 = SIZE_ORDER.includes(size2) ? SIZE_ORDER.indexOf(size2) : Number.MAX_SAFE_INTEGER;
      return comparator1 - comparator2;
    });
  }

  private getAttributeValueFromVariantDetails(attribute: string, variant: Variant): string {
    const attributeValue = variant.variant_values.find(attributeKeyValue => attributeKeyValue.key === attribute).value;
    return attributeValue;
  } */


  isDisabled(sizeVariant: any, index: any) {
    return !(sizeVariant?.value?.in_stock ||
      sizeVariant?.value?.is_allow_back_orders) && index === this.attributesMap.size - 1;
  }

  private initializeBuckets(product: Product) {
    // create a variant like object of the current product
    const variantOfCurrentProduct = getCurrentProductVariantDetail(product);
    this.variantOfCurrentProduct = variantOfCurrentProduct;

    // filter deactivated variants
    const allActiveVariants = this.activeVariants;

    // create the variants array including the current product details
    const allProductVariants = [...allActiveVariants, variantOfCurrentProduct];

    this.prepareVariantBucketsV2(allProductVariants);

    this.populateVariantBucketsV2(allProductVariants);

    this.sortOptions();
  }


  reorderObject(obj: any) {
    const { key, value } = obj;
    return { key, value };
  }

  clearVariantMapValues() {
    const firstVariant = this.activeVariants?.[0];
    if (!firstVariant) return;

    const attributes = this.getAttributes(firstVariant);
    attributes.forEach(attribute => {
      if (this.attributesMap.has(attribute)) {
        this.attributesMap.get(attribute)?.clear();
      }
    })
  }

  private findVariantByAttribute(attribute: string, option: string) {
    const temp = this.variants?.find((variant: Variant) => variant?.variant_values?.some(vv => vv?.key === attribute));
    if (temp && this.attributeSelections.get(attribute)) {
      this.attributeSelections.get(attribute)?.patchValue(option);
    }
  }

  private sortVariants(allVariants: any[]) {
    const sortByOneWay = ((variant1: any, variant2: any) => variant1?.key < variant2?.key ? -1 : 1);
    return allVariants?.find((variant: any) => {
      // ... existing code ...
    });
  }

  private clearAttributeMap(attribute: any) {
    if (this.attributesMap.has(attribute)) {
      this.attributesMap.get(attribute)?.clear();
    }
  }


}

